// Fixed numbers the whole service agrees on. Change them here and nowhere else.
import { type PauseKind, TONES, type Tone } from "../contract/cues";

/** Block size band in code points (DESIGN.md "Refinement internals"). */
export const BLOCK_MIN = 800;
export const BLOCK_MAX = 2000;

/** Pause cues in seconds of silence the stitcher inserts after a Block. The kinds are the contract's. */
export const PAUSES: Readonly<Record<PauseKind, number>> = Object.freeze({
  none: 0,
  short: 0.4,
  medium: 0.8,
  long: 1.5,
});
export type { PauseKind, Tone };
/** Tone cues that become eleven_v3 audio tags; defined once in the contract. */
export { TONES };

/** Seed sent with every ElevenLabs and OpenRouter request. */
export const RENDER_SEED = 815;

/** Job lease and heartbeat (plan "Job worker"). */
export const LEASE_SECONDS = 90;
export const HEARTBEAT_SECONDS = 30;

/**
 * How often a running job may be reclaimed after its lease expired before the
 * worker gives up on it. A handler that kills the process every time would
 * otherwise be reclaimed forever and take every deploy down with it.
 */
export const MAX_RECLAIMS = 5;

/**
 * After the drain deadline, how long a handler that is inside an uninterruptible
 * unit gets to land it before the worker gives up and expires the lease. The
 * shutdown path's cleanup grace is derived from this, so the worker's forced
 * cleanup always finishes before the runtime is disposed underneath it.
 */
export const UNIT_LANDING_GRACE_MS = 4000;

/**
 * How far back the claim loop looks for terminal jobs whose Book and Chapter
 * transition nobody has acknowledged. It bounds the repeat: a hook that keeps
 * failing is retried for this long, and a process that starts after a crash
 * picks up whatever the crashed one did not finish reporting.
 */
export const TERMINAL_SWEEP_WINDOW_MINUTES = 60;

/** How many terminal jobs one sweep reports at most, so a backlog cannot stall a tick. */
export const TERMINAL_SWEEP_LIMIT = 50;

/** Backoff after the first, second, and third real failure of a job. */
export const RETRY_BACKOFF_MINUTES = Object.freeze([1, 5, 20] as const);

/**
 * Characters one text-to-speech request may carry, per model (DESIGN.md
 * "Model"). Built text over the ceiling is a failure, never a split: ADR 0005
 * fixed the Block as the unit of generation, so splitting one would change
 * what the Author approved and heard.
 */
export const REQUEST_CEILING_CHARACTERS = Object.freeze({
  eleven_v3: 5_000,
  eleven_multilingual_v2: 10_000,
} as const);

/** Models that consume an audio tag; on any other model a tone cue is dropped. */
export const AUDIO_TAG_MODELS = Object.freeze(["eleven_v3"] as const);

/** Rendered audio is mono 16-bit PCM, so one frame is two bytes. */
export const PCM_BYTES_PER_FRAME = 2;

/**
 * Silence between Chapters in the M4B (plan "Render and audio"); per-Chapter
 * MP3s carry none. Whole milliseconds, the unit src/render/silence.ts measures
 * every silence in.
 */
export const M4B_CHAPTER_GAP_MS = 1500;

/**
 * Credits per character of Narration Script, by model (DESIGN.md "Estimate").
 * ElevenLabs' help centre states one credit per character for every model
 * inside the app, and the 2026-09-05 comparison measured the same cost per
 * character for `eleven_v3` and `eleven_multilingual_v2`. Tim's pay-as-you-go
 * account reported 0.275 credits per character on the day, so one credit per
 * character is the conservative bound the Estimate shows and is what the
 * Author is asked to confirm.
 */
export const CREDIT_RATE_BY_MODEL = Object.freeze({
  eleven_v3: 1,
  eleven_multilingual_v2: 1,
} as const);

/**
 * Money per thousand credits, in cents: the plan's monthly price divided by
 * its included credits, or the overage rate once those are used up. $0.10 per
 * thousand characters was the single-source API price sheet figure on
 * 2026-09-05 for both supported models.
 */
export const PRICE_PER_1K_CREDITS_CENTS = 10;

/**
 * `eleven_v3`'s stability setting, which DESIGN.md names Creative, Natural,
 * and Robust. A new Book uses Natural, the balanced one.
 */
export const STABILITY = Object.freeze({ creative: 0, natural: 0.5, robust: 1 } as const);
export const DEFAULT_STABILITY = STABILITY.natural;
